import { useEffect, useMemo, useState, type ReactNode } from "react";
import { exportAnswerShareSubmissions } from "../../api/answerShare";
import { ApiError } from "../../api/client";
import type {
  SubmissionsExportColumns,
  SubmissionsExportFormat,
  SubmissionsExportMetaKey,
} from "../../types/submission";
import { AdminDateRangePicker } from "../../components/admin/AdminDateRangePicker";
import { SubmissionsExportMenu } from "../../components/admin/SubmissionsExportMenu";
import { resolveExportFieldParams } from "../../lib/forms/submissionsExport";

interface AnswerShareExportProps {
  token: string;
  accessToken: string;
  columns: SubmissionsExportColumns;
  columnsLoading: boolean;
  from: string;
  to: string;
  onRangeChange: (range: { from: string; to: string }) => void;
  onAccessExpired: () => void;
  onError: (message: string | null) => void;
  extras?: ReactNode;
}

type ExportFormat = SubmissionsExportFormat;

export function AnswerShareExport({
  token,
  accessToken,
  columns,
  columnsLoading,
  from,
  to,
  onRangeChange,
  onAccessExpired,
  onError,
  extras,
}: AnswerShareExportProps) {
  const [format, setFormat] = useState<ExportFormat>("xlsx");
  const [selectedMetaKeys, setSelectedMetaKeys] = useState<
    Set<SubmissionsExportMetaKey>
  >(() => new Set());
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [exporting, setExporting] = useState(false);

  const metaKeys = useMemo(
    () => columns.meta.map((column) => column.key),
    [columns.meta],
  );
  const fieldIds = useMemo(
    () => columns.fields.map((field) => field.id),
    [columns.fields],
  );
  const fieldIdsKey = fieldIds.join(",");

  useEffect(() => {
    setSelectedFieldIds(
      new Set(fieldIdsKey ? fieldIdsKey.split(",").map(Number) : []),
    );
  }, [fieldIdsKey]);

  const allMetaSelected =
    metaKeys.length > 0 && selectedMetaKeys.size === metaKeys.length;
  const allFieldsSelected =
    fieldIds.length > 0 && selectedFieldIds.size === fieldIds.length;
  const allColumnsSelected =
    (metaKeys.length === 0 || allMetaSelected) &&
    (fieldIds.length === 0 || allFieldsSelected);
  const noneSelected =
    selectedMetaKeys.size === 0 && selectedFieldIds.size === 0;
  const selectedCount = selectedMetaKeys.size + selectedFieldIds.size;
  const totalCount = metaKeys.length + fieldIds.length;

  const toggleMetaKey = (
    metaKey: SubmissionsExportMetaKey,
    checked: boolean,
  ) => {
    setSelectedMetaKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(metaKey);
      else next.delete(metaKey);
      return next;
    });
  };

  const toggleField = (fieldId: number, checked: boolean) => {
    setSelectedFieldIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(fieldId);
      else next.delete(fieldId);
      return next;
    });
  };

  const handleExport = async (orderedFieldIds: number[]) => {
    onError(null);
    setExporting(true);

    try {
      const meta =
        selectedMetaKeys.size > 0
          ? metaKeys.filter((key) => selectedMetaKeys.has(key))
          : undefined;
      const { fieldIds: exportFieldIds, order } = resolveExportFieldParams(
        fieldIds,
        orderedFieldIds,
        selectedFieldIds,
      );

      await exportAnswerShareSubmissions(token, accessToken, {
        format,
        from: from || undefined,
        to: to || undefined,
        metaKeys: meta,
        fieldIds: exportFieldIds,
        order,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onAccessExpired();
      } else if (err instanceof ApiError) {
        onError(err.message || "Не удалось экспортировать");
      } else {
        onError("Не удалось скачать файл экспорта");
      }
    } finally {
      setExporting(false);
    }
  };

  if (columnsLoading) {
    return (
      <>
        <p className="font-onest text-xs text-slate-500">Загрузка экспорта…</p>
        {extras}
      </>
    );
  }

  if (totalCount === 0) return extras ?? null;

  return (
    <SubmissionsExportMenu
      idPrefix="share-export"
      format={format}
      onFormatChange={setFormat}
      columns={columns}
      selectedMetaKeys={selectedMetaKeys}
      selectedFieldIds={selectedFieldIds}
      onToggleMeta={toggleMetaKey}
      onToggleField={toggleField}
      onSelectAll={() => {
        setSelectedMetaKeys(new Set(metaKeys));
        setSelectedFieldIds(new Set(fieldIds));
      }}
      onClearAll={() => {
        setSelectedMetaKeys(new Set());
        setSelectedFieldIds(new Set());
      }}
      selectedCount={selectedCount}
      totalCount={totalCount}
      allSelected={allColumnsSelected}
      exporting={exporting}
      downloadDisabled={noneSelected}
      onDownload={(orderedFieldIds) => void handleExport(orderedFieldIds)}
      hint="Порядок в файле: № → служебные столбцы → поля формы. Поля можно перетащить. Служебные столбцы — только если отмечены."
    >
      <AdminDateRangePicker from={from} to={to} onChange={onRangeChange} />
      {extras}
    </SubmissionsExportMenu>
  );
}
